import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { X } from 'lucide-react';
import { galleryImages } from '../data/mock';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function GalleryPage() {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const categories = ['All', ...new Set(galleryImages.map((img) => img.category))];
  const filtered =
    selectedFilter === 'All'
      ? galleryImages
      : galleryImages.filter((img) => img.category === selectedFilter);

  return (
    <main className="pt-20">
      {/* Header */}
      <section className="py-16 bg-[rgb(17,17,19)] relative">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <FadeInSection>
            <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
              Gallery
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
              Life at Scale Up Academy
            </h1>
            <p className="text-[rgb(161,161,170)] text-lg max-w-2xl">
              A visual glimpse into our academic environment, faculty engagement,
              and student journey.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedFilter(cat)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedFilter === cat
                  ? 'bg-[#F37021] text-white'
                  : 'bg-[rgb(26,28,30)] text-[rgb(218,218,218)] border border-[rgba(255,255,255,0.1)] hover:border-[#F37021] hover:text-[#F37021]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="py-8 pb-24 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((img) => (
                <motion.div
                  key={img.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setLightbox(img)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)] group-hover:border-[rgba(243,112,33,0.35)] transition-all group-hover:-translate-y-1">
                    <img
                      src={img.image}
                      alt={img.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block mb-1 px-3 py-1 rounded-lg bg-[rgba(243,112,33,0.15)] text-[#F37021] text-xs font-semibold">
                        {img.category}
                      </span>
                      <h3 className="text-white font-medium text-sm leading-snug">
                        {img.title}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <button
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
              onClick={() => setLightbox(null)}
            >
              <X size={20} />
            </button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.image}
                alt={lightbox.title}
                className="w-full rounded-2xl mb-6"
              />
              <h3 className="text-2xl font-bold text-white text-center">
                {lightbox.title}
              </h3>
              <p className="text-center text-[#F37021] text-sm mt-1">
                {lightbox.category}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
